import { getQueryTimeseriesForNodeUseCase } from "@src/cloud-connect/use-cases/get-query-timeseries-for-node";
import type {
  MachineState,
  MachineDataResponse,
} from "@src/types/factory-dashboard";

export type {
  ModeColor,
  ModeConfig,
  MachineOverviewDefinition,
  MachineState,
  MachineDataResponse,
} from "@src/types/factory-dashboard";

interface SensorValue {
  value: number | string | null;
  time: string;
}

type OptionalMachineOverviewField =
  | "productionDatasetName"
  | "moldCatActualDescription"
  | "filename"
  | "customer"
  | "material";

type MachineNodeMapping = Record<
  "opMode" | "actCyclesDone" | "setCyclesCompleteToDo",
  string
> &
  Partial<Record<OptionalMachineOverviewField, string>>;

interface MachineSensorEntry {
  meta_data: Record<string, unknown>;
  values: { time: string; value: number | string | null }[];
  timeseries_type: string;
}

const MACHINE_OVERVIEW_FIELDS = [
  "opMode",
  "actCyclesDone",
  "setCyclesCompleteToDo",
  "productionDatasetName",
  "moldCatActualDescription",
  "filename",
  "customer",
  "material",
] as const;

type MachineOverviewField = (typeof MACHINE_OVERVIEW_FIELDS)[number];

function normalizeSensorEntry(entry: MachineSensorEntry): SensorValue {
  const latest = entry.values[0];
  return {
    value: latest?.value ?? null,
    time: latest?.time ?? "",
  };
}

const createInitialMachineState = (): MachineState =>
  MACHINE_OVERVIEW_FIELDS.reduce((acc, field) => {
    acc[field] = { value: null, time: "" };
    return acc;
  }, {} as MachineState);

const getFieldNodePairs = (
  nodes: MachineNodeMapping,
): ReadonlyArray<{ field: MachineOverviewField; nodeId: string }> =>
  MACHINE_OVERVIEW_FIELDS.reduce<
    Array<{ field: MachineOverviewField; nodeId: string }>
  >((pairs, field) => {
    const nodeId = nodes[field];
    if (nodeId) {
      pairs.push({ field, nodeId });
    }
    return pairs;
  }, []);

function buildMachineState(
  entries: MachineSensorEntry[],
  fields: readonly MachineOverviewField[],
): MachineState {
  return fields.reduce((acc, field, index) => {
    const entry = entries[index];
    if (entry) {
      acc[field] = normalizeSensorEntry(entry);
    }
    return acc;
  }, createInitialMachineState());
}

export function hasMachineData(state: MachineState | undefined): boolean {
  return !!state && Object.values(state).some((v) => v.value != null);
}

export const fetchMachineData = async (
  group: string,
  nodes: MachineNodeMapping,
): Promise<MachineDataResponse> => {
  const fieldNodePairs = getFieldNodePairs(nodes);
  const now = new Date();
  const thirtyMinutesAgo = new Date(now.getTime() - 30 * 60 * 1000);

  const queryParams = {
    limit: 1,
    from: thirtyMinutesAgo.toISOString(),
    to: now.toISOString(),
    limit_order: "DESC" as const,
  };

  try {
    const sensorResults = await Promise.all([
      ...fieldNodePairs.map(({ nodeId }) =>
        getQueryTimeseriesForNodeUseCase({
          group,
          source: nodeId,
          ...queryParams,
        }),
      ),
    ]);

    if (sensorResults.some((res) => !res.success)) {
      console.error(
        `One or more queries failed for machine ${group}:`,
        sensorResults,
      );
      return {};
    }

    const entries = sensorResults.map((res) => res.data as MachineSensorEntry);

    return {
      [group]: buildMachineState(
        entries,
        fieldNodePairs.map((pair) => pair.field),
      ),
    };
  } catch (err) {
    console.error(`Failed to fetch data for machine ${group}`, err);
    return {};
  }
};
